// 🔄 이월금 서비스 - Firestore CRUD

import {
    collection,
    doc,
    getDocs,
    getDoc,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { getAuth } from 'firebase/auth';
import type {
    MonthlyCarryover,
    AnnualCarryover,
    AnnualCarryoverFormData,
    CurrencyBalances
} from '../types/carryover';
import { EMPTY_BALANCES } from '../types/carryover';

// 컬렉션 참조
const annualCarryoverRef = collection(db, 'annual-carryover');
const monthlyCarryoverRef = collection(db, 'monthly-carryover');

// 통화별 잔액 합산 헬퍼
export const addBalances = (a: CurrencyBalances, b: CurrencyBalances): CurrencyBalances => ({
    HUF: (a.HUF || 0) + (b.HUF || 0),
    EUR: (a.EUR || 0) + (b.EUR || 0),
    USD: (a.USD || 0) + (b.USD || 0),
    KRW: (a.KRW || 0) + (b.KRW || 0),
    GBP: (a.GBP || 0) + (b.GBP || 0),
    CAD: (a.CAD || 0) + (b.CAD || 0),
});

// 통화별 잔액 차감 헬퍼
export const subtractBalances = (a: CurrencyBalances, b: CurrencyBalances): CurrencyBalances => ({
    HUF: (a.HUF || 0) - (b.HUF || 0),
    EUR: (a.EUR || 0) - (b.EUR || 0),
    USD: (a.USD || 0) - (b.USD || 0),
    KRW: (a.KRW || 0) - (b.KRW || 0),
    GBP: (a.GBP || 0) - (b.GBP || 0),
    CAD: (a.CAD || 0) - (b.CAD || 0),
});

// 통화별 잔액 포맷팅
export const formatBalances = (balances: CurrencyBalances): string => {
    const parts: string[] = [];
    if (balances.HUF) parts.push(`${balances.HUF.toLocaleString()} Ft`);
    if (balances.EUR) parts.push(`€${balances.EUR.toLocaleString()}`);
    if (balances.USD) parts.push(`$${balances.USD.toLocaleString()}`);
    if (balances.KRW) parts.push(`₩${balances.KRW.toLocaleString()}`);
    if (balances.GBP) parts.push(`£${balances.GBP.toLocaleString()}`);
    if (balances.CAD) parts.push(`CA$${balances.CAD.toLocaleString()}`);
    return parts.length > 0 ? parts.join(', ') : '0';
};

/**
 * 이월금 서비스
 */
export const carryoverService = {
    // =====================
    // 연간 이월금 (연도 초기 설정)
    // =====================

    // 연간 이월금 생성
    async createAnnualCarryover(formData: AnnualCarryoverFormData): Promise<string> {
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user) {
            throw new Error('로그인이 필요합니다.');
        }

        const docData = {
            year: formData.year,
            sourceYear: formData.sourceYear,
            balances: formData.balances,
            isConfirmed: false,
            notes: formData.notes || '',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        };

        const docRef = await addDoc(annualCarryoverRef, docData);
        return docRef.id;
    },

    // 연간 이월금 조회 (특정 연도)
    async getAnnualCarryover(year: number): Promise<AnnualCarryover | null> {
        const q = query(annualCarryoverRef, where('year', '==', year));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
            return null;
        }

        const doc = snapshot.docs[0];
        return {
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate(),
            updatedAt: doc.data().updatedAt?.toDate(),
            confirmedAt: doc.data().confirmedAt?.toDate(),
        } as AnnualCarryover;
    },

    // 모든 연간 이월금 조회
    async getAllAnnualCarryovers(): Promise<AnnualCarryover[]> {
        const q = query(annualCarryoverRef, orderBy('year', 'desc'));
        const snapshot = await getDocs(q);

        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate(),
            updatedAt: doc.data().updatedAt?.toDate(),
            confirmedAt: doc.data().confirmedAt?.toDate(),
        } as AnnualCarryover));
    },

    // 연간 이월금 확정
    async confirmAnnualCarryover(id: string): Promise<void> {
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user) {
            throw new Error('로그인이 필요합니다.');
        }

        const docRef = doc(annualCarryoverRef, id);
        await updateDoc(docRef, {
            isConfirmed: true,
            confirmedAt: serverTimestamp(),
            confirmedBy: user.email,
            updatedAt: serverTimestamp(),
        });
    },

    // 연간 이월금 수정
    async updateAnnualCarryover(id: string, formData: Partial<AnnualCarryoverFormData>): Promise<void> {
        const docRef = doc(annualCarryoverRef, id);
        await updateDoc(docRef, {
            ...formData,
            updatedAt: serverTimestamp(),
        });
    },

    // 연간 이월금 삭제
    async deleteAnnualCarryover(id: string): Promise<void> {
        const docRef = doc(annualCarryoverRef, id);
        await deleteDoc(docRef);
    },

    // =====================
    // 월별 이월금
    // =====================

    // 월별 이월금 조회 (특정 년/월)
    async getMonthlyCarryover(year: number, month: number): Promise<MonthlyCarryover | null> {
        const q = query(
            monthlyCarryoverRef,
            where('year', '==', year),
            where('month', '==', month)
        );
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
            return null;
        }

        const doc = snapshot.docs[0];
        return {
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate(),
            updatedAt: doc.data().updatedAt?.toDate(),
            confirmedAt: doc.data().confirmedAt?.toDate(),
        } as MonthlyCarryover;
    },

    // 특정 연도의 모든 월별 이월금 조회
    async getYearlyMonthlyCarryovers(year: number): Promise<MonthlyCarryover[]> {
        const q = query(
            monthlyCarryoverRef,
            where('year', '==', year),
            orderBy('month', 'asc')
        );
        const snapshot = await getDocs(q);

        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate(),
            updatedAt: doc.data().updatedAt?.toDate(),
            confirmedAt: doc.data().confirmedAt?.toDate(),
        } as MonthlyCarryover));
    },

    // 월별 이월금 계산 및 생성/업데이트
    async calculateMonthlyCarryover(
        year: number,
        month: number,
        monthlyIncome: CurrencyBalances,
        monthlyExpense: CurrencyBalances
    ): Promise<string> {
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user) {
            throw new Error('로그인이 필요합니다.');
        }

        // 전월 잔액 가져오기
        let previousBalance: CurrencyBalances = { ...EMPTY_BALANCES };

        if (month === 1) {
            // 1월인 경우 연간 이월금에서 가져옴
            const annualCarryover = await this.getAnnualCarryover(year);
            if (annualCarryover) {
                previousBalance = annualCarryover.balances;
            }
        } else {
            // 그 외에는 전월 이월금에서 가져옴
            const prevMonthCarryover = await this.getMonthlyCarryover(year, month - 1);
            if (prevMonthCarryover) {
                previousBalance = prevMonthCarryover.nextBalance;
            }
        }

        // 순수익 계산
        const monthlyNet = subtractBalances(monthlyIncome, monthlyExpense);

        // 다음달 이월액 계산
        const nextBalance = addBalances(previousBalance, monthlyNet);

        // 기존 데이터 확인
        const existing = await this.getMonthlyCarryover(year, month);

        const docData = {
            year,
            month,
            previousBalance,
            monthlyIncome,
            monthlyExpense,
            monthlyNet,
            nextBalance,
            isConfirmed: false,
            updatedAt: serverTimestamp(),
        };

        if (existing) {
            // 업데이트
            const docRef = doc(monthlyCarryoverRef, existing.id);
            await updateDoc(docRef, docData);
            return existing.id;
        } else {
            // 새로 생성
            const newDocRef = await addDoc(monthlyCarryoverRef, {
                ...docData,
                createdAt: serverTimestamp(),
            });
            return newDocRef.id;
        }
    },

    // 월별 이월금 확정
    async confirmMonthlyCarryover(id: string): Promise<void> {
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user) {
            throw new Error('로그인이 필요합니다.');
        }

        const docRef = doc(monthlyCarryoverRef, id);
        await updateDoc(docRef, {
            isConfirmed: true,
            confirmedAt: serverTimestamp(),
            confirmedBy: user.email,
            updatedAt: serverTimestamp(),
        });
    },
};
